/* ============================================================================
   LONG-LEAD ITEMS — the maths behind "order it in time".

   A long-lead item is tied to the programme activities that cannot start
   without it. Its need date is the earliest of those starts; its order-by
   date is that minus the lead time and a safety buffer. Because it reads the
   live programme, the order-by date moves the day the programme moves — and
   the page, the reminders and the call-forward all read from here.
   ========================================================================== */
import type { GanttTask, LongLeadItem, Project } from "./types";

export const DEFAULT_BUFFER_DAYS = 14;

export type LongLeadState =
  | "cancelled" | "on-site" | "in-transit" | "ordered" | "eta-late"
  | "late" | "order-now" | "soon" | "planned" | "unlinked";

const DAY = 86_400_000;
const iso = (t: number) => new Date(t).toISOString().slice(0, 10);
const at = (d: string) => +new Date(d + "T12:00:00Z");

export interface LongLeadSchedule {
  tasks: GanttTask[];
  requiredOn: string | null;     // the first linked activity's start, or the manual date
  orderBy: string | null;        // requiredOn − (lead + buffer)
  daysToOrder: number | null;    // from today; negative = already late
  state: LongLeadState;
  etaSlipDays: number | null;    // how far the supplier's ETA lands past the need date
}

export function longLeadSchedule(p: Project, item: LongLeadItem, now = Date.now()): LongLeadSchedule {
  const tasks = (item.taskIds ?? []).map((id) => p.tasks.find((t) => t.id === id)).filter((t): t is GanttTask => !!t);
  const requiredOn = tasks.length ? [...tasks.map((t) => t.start)].sort()[0] : (item.requiredOn ?? null);
  const lead = Math.max(0, item.leadDays || 0) + (item.bufferDays ?? DEFAULT_BUFFER_DAYS);
  const orderBy = requiredOn ? iso(at(requiredOn) - lead * DAY) : null;
  const daysToOrder = orderBy ? Math.round((at(orderBy) - now) / DAY) : null;
  const etaSlipDays = item.eta && requiredOn ? Math.round((at(item.eta) - at(requiredOn)) / DAY) : null;
  let state: LongLeadState;
  if (item.status === "cancelled") state = "cancelled";
  else if (item.status === "on-site") state = "on-site";
  else if (item.status === "in-transit" || item.status === "ordered") state = etaSlipDays !== null && etaSlipDays > 0 ? "eta-late" : item.status;
  else if (daysToOrder === null) state = "unlinked";
  else if (daysToOrder < 0) state = "late";
  else if (daysToOrder <= 7) state = "order-now";
  else if (daysToOrder <= 21) state = "soon";
  else state = "planned";
  return { tasks, requiredOn, orderBy, daysToOrder, state, etaSlipDays };
}

export const LL_STATE_LABEL: Record<LongLeadState, string> = {
  cancelled: "Cancelled", "on-site": "On site", "in-transit": "In transit", ordered: "Ordered",
  "eta-late": "ETA after need date", late: "Order overdue", "order-now": "Order now",
  soon: "Order within 3 weeks", planned: "Planned", unlinked: "No activity linked",
};
export const LL_STATE_TONE: Record<LongLeadState, string> = {
  cancelled: "var(--ink-4)", "on-site": "var(--ok)", "in-transit": "var(--sky)", ordered: "var(--sky)",
  "eta-late": "var(--danger)", late: "var(--danger)", "order-now": "var(--danger)",
  soon: "var(--warn)", planned: "var(--ink-3)", unlinked: "var(--ink-3)",
};

export function longLeadSummary(p: Project, now = Date.now()) {
  const items = (p.longLead ?? []).filter((i) => i.status !== "cancelled");
  const rows = items.map((item) => ({ item, sch: longLeadSchedule(p, item, now) }));
  const count = (...st: LongLeadState[]) => rows.filter((x) => st.includes(x.sch.state)).length;
  return {
    total: items.length,
    orderNow: count("late", "order-now"),
    soon: count("soon"),
    ordered: count("ordered", "in-transit"),
    onSite: count("on-site"),
    etaLate: count("eta-late"),
    unlinked: count("unlinked"),
    rows,
  };
}

/** The probe names activities in words; map them onto programme task ids —
    exact match first, then containment either way. */
export function matchTasks(p: Project, names: string[]): string[] {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9一-鿿]+/g, " ").trim();
  const out = new Set<string>();
  for (const n of names ?? []) {
    const q = norm(n);
    if (!q || q.length < 3) continue;
    const exact = p.tasks.find((t) => norm(t.name) === q);
    if (exact) { out.add(exact.id); continue; }
    for (const t of p.tasks) {
      const tn = norm(t.name);
      if (tn.length >= 3 && (tn.includes(q) || q.includes(tn))) out.add(t.id);
    }
  }
  return [...out];
}
